/**
 * Auth context — the console's single source of who-is-signed-in.
 *
 * Holds the `AdminApi` (built once, transport chosen by `config`), the current session loaded
 * from whichever store holds one, and the login/logout actions. The api client's `getToken`
 * reads a ref so it always sees the live token, and its `onUnauthorized` drops the session — so
 * any 401, from any page, lands the operator back on the login screen with no stale state.
 *
 * ## Remember me, and the two places it has to work
 *
 * `login` passes `remember` straight through to `saveSession`, which decides between the
 * tab-scoped default and the device store. That is only half of it. The access token is an
 * hour whatever the box says, so keeping an operator signed in for thirty days means spending
 * the rotating remember-me token for a fresh one, and there are exactly two moments that has to
 * happen:
 *
 *   1. **On boot**, when the tab was closed overnight and the stored access token is already
 *      dead but the lineage is not. Without this the feature does nothing visible: an operator
 *      who ticked the box is still bounced to the login screen every morning.
 *   2. **While the tab is open**, shortly before the hour runs out. Proactive rather than
 *      reactive on a 401, because a reactive refresh has to replay the request that failed, and
 *      replaying a request that might have been a `POST /balance` is not a thing to build.
 *
 * A refresh answers with an `AdminSession` — two tokens — and **not** an `AdminLoginResponse`.
 * The operator's identity is not in it and is not meant to be: this context has held it since
 * the login response, and it is carried across a rotation by `rotatedCredentials` rather than
 * read back off the wire. Getting that wrong is silent, survives every mock, and ends the
 * thirty days at the first rotation; the whole story is in that function's docblock.
 *
 * ## Why the single-flight guard is load-bearing
 *
 * The remember-me token is **single-use with reuse detection**: spending it twice looks exactly
 * like a stolen token being replayed, and the API's correct response is to burn the whole
 * lineage and sign the operator out everywhere. React's `StrictMode` runs effects twice in
 * development, which would do precisely that on the first page load. `refreshing` is a
 * module-level promise rather than a ref for that reason — a ref is per-mount, and the two
 * `StrictMode` mounts each get their own.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { AdminApi } from '../api/client.js';
import { createTransport } from '../api/config.js';
import {
  clearSession,
  isLive,
  isRenewable,
  loadSession,
  rotatedCredentials,
  saveSession,
  type SessionCredentials,
  type StoredSession,
} from '../api/session.js';
import type { AdminLoginRequest } from '../contracts/types.js';

interface AuthValue {
  readonly session: StoredSession | null;
  readonly api: AdminApi;
  /**
   * True while a stored remember-me session is being renewed on boot. The app shows a quiet
   * loading screen rather than the login form, so an operator who is about to be signed in
   * never sees a sign-in prompt flash past.
   */
  readonly booting: boolean;
  /**
   * Sign in. `remember` persists the session on this device for up to 30 days instead of only
   * for this tab, and asks the API for the lineage that makes that real.
   */
  login(credentials: AdminLoginRequest, remember?: boolean): Promise<void>;
  logout(): void;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * The one refresh that may be in flight, shared across every mount in this tab.
 *
 * Module scope, deliberately. See the note at the top: a per-mount ref does not survive a
 * `StrictMode` double-effect, and the cost of getting this wrong is not a wasted request but
 * an operator signed out of every tab by the API's reuse detection.
 */
let refreshing: Promise<StoredSession | null> | null = null;

/** Refresh the session from the stored lineage, at most once at a time. */
function refreshOnce(api: AdminApi, stored: StoredSession): Promise<StoredSession | null> {
  refreshing ??= (async () => {
    try {
      const token = stored.rememberToken;
      if (token === undefined || token === '') return null;
      const res = await api.refreshSession({ rememberToken: token });
      // The identity comes from the session being replaced, not from the response: a refresh
      // answers with tokens only. See `rotatedCredentials` for what reading it off the response
      // instead did to the thirty days.
      const next = rotatedCredentials(stored, res);
      // The original ceiling is carried forward rather than recomputed. Rotation renews the
      // token, never the deadline: otherwise thirty days of steady use becomes an indefinite
      // session, one refresh at a time.
      saveSession(next, true, undefined, Date.now(), stored.rememberUntil);
      return loadSession() ?? { ...next, remembered: true, rememberUntil: stored.rememberUntil };
    } catch {
      // A refusal here is final: the lineage is spent, expired, or was burnt by a replay. There
      // is nothing to retry with, so the session goes and the operator signs in again.
      clearSession();
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/**
 * What the very first render starts from, worked out once.
 *
 * A stored session whose access token has expired is deliberately **not** handed straight to
 * the app: every request it made would 401, and the operator would watch a page load and then
 * throw them out. It becomes `booting` instead, and the renewal decides which way it goes.
 */
function bootState(): { session: StoredSession | null; booting: boolean } {
  const stored = loadSession();
  if (stored === null) return { session: null, booting: false };
  const now = Date.now();
  if (isLive(stored, now)) return { session: stored, booting: false };
  return { session: null, booting: isRenewable(stored, now) };
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  // Computed once, synchronously, so the first render already knows whether a renewal is
  // coming. Deciding it in an effect would let one frame of the login screen through first.
  const [initial] = useState(bootState);
  const [session, setSession] = useState<StoredSession | null>(initial.session);
  const [booting, setBooting] = useState(initial.booting);
  const tokenRef = useRef<string | null>(initial.session?.accessToken ?? null);

  const setActive = useCallback((next: StoredSession | null) => {
    tokenRef.current = next?.accessToken ?? null;
    setSession(next);
  }, []);

  const api = useMemo(
    () =>
      new AdminApi({
        transport: createTransport(),
        getToken: () => tokenRef.current,
        onUnauthorized: () => {
          clearSession();
          setActive(null);
        },
      }),
    [setActive],
  );

  const logout = useCallback(() => {
    /*
     * Burn the server-side lineage, then clear locally — and clear locally whatever the call
     * does. A network failure must never leave an operator still signed in on a shared machine,
     * so the local wipe is not conditional on the request landing. The catch is deliberate and
     * empty: there is no useful thing to tell somebody who has already signed out.
     */
    const token = session?.rememberToken;
    if (token !== undefined && token !== '') {
      void api.logoutSession({ rememberToken: token }).catch(() => undefined);
    }
    clearSession();
    setActive(null);
  }, [api, session, setActive]);

  const login = useCallback(
    async (credentials: AdminLoginRequest, remember = false) => {
      const res = await api.login(credentials);
      const next: SessionCredentials = {
        accessToken: res.accessToken,
        accessTokenExpiresAt: res.accessTokenExpiresAt,
        me: res.admin,
        rememberToken: res.rememberToken,
        rememberTokenExpiresAt: res.rememberTokenExpiresAt,
      };
      saveSession(next, remember);
      // Read back, so what the app holds is exactly what a reload would find — the ceiling
      // included, which only `saveSession` knows. The fallback matters: a browser can refuse a
      // storage write outright (private mode, quota), and a sign-in that then behaved as though
      // it had failed would be worse than one that simply does not survive a reload. The token
      // is in memory either way.
      setActive(loadSession() ?? { ...next, remembered: remember, rememberUntil: null });
    },
    [api, setActive],
  );

  // ── boot: renew a session whose access token died while the tab was closed ──
  useEffect(() => {
    if (!booting) return;
    const stored = loadSession();
    if (stored === null || !isRenewable(stored, Date.now())) {
      setBooting(false);
      return;
    }
    let live = true;
    void refreshOnce(api, stored).then((next) => {
      if (!live) return;
      setActive(next);
      setBooting(false);
    });
    return () => {
      live = false;
    };
  }, [api, booting, setActive]);

  // ── while open: renew shortly before the hour runs out ──
  useEffect(() => {
    if (session === null || !session.remembered) return;
    if (session.rememberToken === undefined || session.rememberToken === '') return;

    const expiresAt = Date.parse(session.accessTokenExpiresAt);
    if (Number.isNaN(expiresAt)) return;
    /*
     * A minute of headroom, and never a negative delay.
     *
     * The headroom is what keeps a request from setting off with a token that expires while it
     * is in flight. `setTimeout` clamps a negative delay to zero, which is the behaviour wanted
     * for an already-expired token, but `Math.max` says so rather than leaving it to a
     * platform detail. The 2^31 clamp is the other end: a delay past that wraps in every engine
     * and would fire immediately, in a loop.
     */
    const delay = Math.min(Math.max(expiresAt - Date.now() - 60_000, 0), 2 ** 31 - 1);
    const timer = window.setTimeout(() => {
      void refreshOnce(api, session).then((next) => {
        setActive(next);
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [api, session, setActive]);

  const value = useMemo<AuthValue>(
    () => ({ session, api, booting, login, logout }),
    [session, api, booting, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
